import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Badge } from '../../badge';
import { Counter } from '../../counter';
import { Icon } from '../../icon';
import { Image } from '../../image';
import { cropText } from '../../lib/crop-text';
import { Typography } from '../../typography';
import { ItemsDisplayMode } from '../config';
import { useHeaderContext } from '../context';

import styles from './item.scss';

type HeaderItemProps = RefAttributes<HTMLButtonElement | HTMLLinkElement> &
  HTMLAttributes<HTMLButtonElement | HTMLLinkElement> & {
    icon?: {
      name?: string;
      activeName?: string;
      asImage?: boolean;
    };
    label?: {
      text?: string;
      [key: string]: unknown;
    };
    badge?: {
      text?: string;
      [key: string]: unknown;
    };
    active?: boolean;
    counter?: {
      value?: number | string;
      className?: string;
      asNotificationBadge?: boolean;
      [key: string]: unknown;
    };
    href?: string;
    dataTestId?: string;
    onClick?: React.MouseEventHandler;
  };

const COMPONENT_ID = 'modulor-header3-item';
const MAX_LABEL_LENGTH = 20;

const DEFAULT_COUNTER = {} as {
  value?: number | string;
  className?: string;
  asNotificationBadge?: boolean;
  [K: string]: any;
};
export const Header3Item: ForwardRefExoticComponent<HeaderItemProps> = forwardRef<
  HTMLButtonElement | HTMLLinkElement,
  HeaderItemProps
>(
  (
    {
      icon: { name: iconToken = '', activeName: activeIconToken = '', asImage = false } = {},
      label: { text: labelText = '', ...labelProps } = {},
      badge: { text: badgeText = '', ...badgeProps } = {},
      active,
      counter: {
        value: counterValue = null,
        className: counterClassName,
        asNotificationBadge: counterAsNotificationBadge,
        ...counterProps
      } = DEFAULT_COUNTER,
      dataTestId = COMPONENT_ID,
      style,
      className,
      onClick,
      href,
      ...rest
    },
    ref,
  ) => {
    const { itemsDisplayMode, closeMoreDropdown } = useHeaderContext();

    const withIcon =
      (iconToken || (active && activeIconToken)) &&
      [ItemsDisplayMode.Icons, ItemsDisplayMode.IconsTitles].includes(itemsDisplayMode);
    const withLabel =
      labelText &&
      [ItemsDisplayMode.Titles, ItemsDisplayMode.IconsTitles].includes(itemsDisplayMode);
    const withCounter = counterValue !== undefined && counterValue !== null;
    const withBadge = badgeText;

    const [iconName, setIconName] = useState('');

    const onClickHandler = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        closeMoreDropdown();
        onClick?.(event);
      },
      [closeMoreDropdown, onClick],
    ) as React.MouseEventHandler<HTMLButtonElement> & React.MouseEventHandler<HTMLAnchorElement>;

    useEffect(() => {
      if (!withIcon || asImage) return;

      const resolvedIconName = active
        ? activeIconToken || iconToken.replace('outlined', 'filled')
        : iconToken;
      setIconName(resolvedIconName);
    }, [active, withIcon, iconToken, activeIconToken, asImage]);

    const Tag = href ? 'a' : 'button';

    if (!withIcon && !withLabel) {
      return null;
    }

    return (
      <Tag
        className={classNames(className, styles['header-item'], {
          [styles.active]: active,
          [styles['no-label']]: !withLabel,
        })}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        {...(href && { href })}
        style={style}
        onClick={onClickHandler}
        {...rest}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}>
        {withIcon &&
          (asImage ? (
            <Image
              dataTestId={`${dataTestId}-image`}
              width="20"
              height="20"
              name={iconToken}
              className={styles.icon}
            />
          ) : (
            <Icon
              dataTestId={`${dataTestId}-icon`}
              size={20}
              name={iconName}
              className={styles.icon}
            />
          ))}

        <Typography
          dataTestId={`${dataTestId}-label`}
          variant="subhead-regular"
          className={classNames(styles.label, {
            [styles.hidden]: !withLabel,
            [styles['no-icon']]: !withIcon,
          })}
          {...labelProps}>
          {cropText(labelText, MAX_LABEL_LENGTH)}
        </Typography>

        {withCounter && (
          <span
            className={classNames(styles.counter, counterClassName, {
              [styles['notification-badge']]: counterAsNotificationBadge,
            })}
            {...counterProps}>
            <Counter
              value={!counterAsNotificationBadge ? counterValue : 0}
              dataTestId={`${dataTestId}-counter`}
              notEmpty={!counterAsNotificationBadge}
            />
          </span>
        )}

        {withBadge && (
          <Badge
            dataTestId={`${dataTestId}-badge`}
            shape="new"
            text={badgeText}
            className={styles.badge}
            font="caption-2-medium"
            badgeColor="icon-notification"
            contentColor="text-always-dark"
            {...badgeProps}
          />
        )}
      </Tag>
    );
  },
);

Header3Item.displayName = 'Header3Item';
